import React from 'react';
import ReactDom from 'react-dom';
import axios from 'helpers/axios';
import useInView from 'react-cool-inview';

import { Container, Label, ModalPosition, Spinner } from 'ui/components/styled';
import { ConfirmationModal } from 'ui/components/styled/NewModal/ConfirmationModal';

import { useCompany, useToasts, useMutableRef, useOpenState, useLayout, useWebSocket } from 'hooks';
import { Loader, Row, ActionButton } from './styled';
import { ConfirmUnarchive } from '../modals';

export const ArchivedCategories = ({ onRefresh, isSmall }) => {
  const ref = React.useRef();
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [perPage] = React.useState(10);
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const [modal, openModal, closeModal] = useOpenState();
  const { errorToast, removeAllToasts } = useToasts();
  const { subscribe, unsubscribe } = useWebSocket();
  const { isLoading: isBusy, setLayoutLoading } = useLayout();
  const { activeCompany } = useCompany();
  const modalRoot = document.getElementById('dashboard-container') || document.body;

  const fetchData = async (page) => {
    setLoading(true);

    try {
      const top = page * perPage;
      const skip = 0;
      const response = await axios.get(
        `/jobs/categories/?$top=${top}&$skip=${skip}&$orderby=name&$order=asc&$cid=${activeCompany}&$archived=true`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotal(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Archived Categories.' });
    }

    setLoading(false);
  };

  const removeCategory = async (category) => {
    try {
      const response = await axios.delete(`/jobs/categories/${category.id}`);

      if (response.status === 204) {
        removeAllToasts();
        refresh();
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Remove Category.' });
    }

    closeModal();
  };

  const unarchiveCategory = async (category, matchOrphans = false) => {
    try {
      if (matchOrphans) setLayoutLoading(true);
      const response = await axios.post(
        `/jobs/categories/${category.id}/unarchive?$matchOrphans=${matchOrphans}`
      );

      if (response.status === 200) {
        removeAllToasts();
        //refresh(true); // will be performed by realtime updates handler
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Unarchive Category.' });
    }

    closeModal();
    setLayoutLoading(false);
  };

  const refresh = (refreshDashboard = false) => {
    fetchData(1);
    if (refreshDashboard && onRefresh) {
      onRefresh();
    }
  };

  React.useEffect(() => {
    fetchData(1);
  }, []);

  const { observe } = useInView({
    root: ref.current,
    onEnter: ({ unobserve }) => {
      if (loading) return;

      const hasMore = data.length < total;
      if (!hasMore) {
        unobserve();
        return;
      }

      // Load more data
      const nextPage = currentPage.current + 1;
      setCurrentPage(nextPage);
      fetchData(nextPage);
    },
  });

  const handleRealtimeUpdates = () => {
    refresh(true);
  };

  React.useEffect(() => {
    if (!data?.length) return;
    subscribe('category:unarchive', handleRealtimeUpdates);

    return () => unsubscribe('category:unarchive', handleRealtimeUpdates);
  }, [data]);

  return (
    <>
      <Container mb="xs">
        <Label color="darkGrey" fontSize="15px">
          Categories
        </Label>
      </Container>
      <Container ref={ref} maxHeight="175px" overflow="auto" mb="l">
        {data.map((category, index) => (
          <Row ref={index === data.length - 1 ? observe : null} key={index}>
            <Label fontWeight={400} fontSize={isSmall ? '12px' : '14px'}>
              {category.name}
            </Label>
            <Container
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-end"
              width="auto"
              height="auto"
            >
              {category.lockedAt && <Spinner size="20px" />}
              <ActionButton
                variant="danger-dark"
                onClick={() => openModal('confirm-remove', { category })}
                disabled={loading || category.lockedAt}
                mx="xs"
              >
                Delete
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={() => openModal('confirm-unarchive', { category })}
                disabled={loading || category.lockedAt}
              >
                Unarchive
              </ActionButton>
            </Container>
          </Row>
        ))}
        {loading && <Loader />}
      </Container>
      {ReactDom.createPortal(
        <>
          <ConfirmationModal
            show={modal.isOpen('confirm-remove')}
            position={ModalPosition.TOP_CENTER}
            top="30%"
            width="35%"
            maxWidth="550px"
            onClose={closeModal}
            onConfirm={() => removeCategory(modal.params?.category)}
            cancelButtonProps={{ variant: 'contrast-primary', boxShadow: 'none' }}
            confirmButtonProps={{ variant: 'danger', boxShadow: 'none', text: 'Yes Delete' }}
            stopClickPropagation
          >
            Are you sure you want to permanently delete category &quot;
            {modal.params?.category?.name} &quot; ? All the monitors associated with this category
            would also be removed.
          </ConfirmationModal>
          <ConfirmUnarchive
            show={modal.isOpen('confirm-unarchive')}
            position={ModalPosition.TOP_CENTER}
            top="25%"
            width="35%"
            maxWidth="550px"
            onClose={closeModal}
            onConfirm={({ checked }) => unarchiveCategory(modal.params?.category, checked)}
            title={`Unarchive category "${modal.params?.category?.name}"`}
            checkboxProps={{
              label: 'Would you like to match orphans with this category monitors?',
            }}
            closeOnOutsideClick={!isBusy}
            stopClickPropagation
          />
        </>,
        modalRoot
      )}
    </>
  );
};
